import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import {
  BudgetRange,
  FormData,
  FormErrors,
  ProjectType,
  TimelineOption,
} from "../types";

interface CollaborationFormProps {
  projectTypes: ProjectType[];
  budgetRanges: BudgetRange[];
  timelineOptions: TimelineOption[];
  selectedCollaborationType: string;
  onSubmit: (data: FormData) => void;
}

const CollaborationForm = ({
  projectTypes,
  budgetRanges,
  timelineOptions,
  selectedCollaborationType,
  onSubmit,
}: CollaborationFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    projectType: "",
    collaborationType: selectedCollaborationType,
    budgetRange: "",
    timeline: "",
    projectTitle: "",
    projectDescription: "",
    technicalRequirements: "",
    goals: "",
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.projectType) {
      newErrors.projectType = "Please select a project type";
    }

    if (!formData.collaborationType) {
      // Auto fallback to a sensible default to match contact UX smoothness
      setFormData((prev) => ({ ...prev, collaborationType: "contract-work" }));
    }

    if (!formData.budgetRange) {
      newErrors.budgetRange = "Please select a budget range";
    }

    if (!formData.timeline) {
      newErrors.timeline = "Please select a timeline";
    }

    if (!formData.projectTitle.trim()) {
      newErrors.projectTitle = "Project title is required";
    }

    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = "Project description is required";
    } else if (formData.projectDescription.trim().length < 50) {
      newErrors.projectDescription =
        "Please provide at least 50 characters describing your project";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions";
    }

    setErrors(newErrors);
    // Focus first error for better feedback
    const firstErrorKey = Object.keys(newErrors)[0] as keyof FormErrors | undefined;
    if (firstErrorKey) {
      const el = document.querySelector(`[name="${String(firstErrorKey)}"]`) as HTMLElement | null;
      if (el && typeof el.focus === "function") {
        el.focus();
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const res = await fetch("https://formspree.io/f/xzzngloo", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          projectType: formData.projectType,
          collaborationType: formData.collaborationType,
          budgetRange: formData.budgetRange,
          timeline: formData.timeline,
          projectTitle: formData.projectTitle,
          projectDescription: formData.projectDescription,
          technicalRequirements: formData.technicalRequirements,
          goals: formData.goals,
          agreeToTerms: formData.agreeToTerms,
          _origin: "collaboration",
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      // Trigger success callback and reset form like ContactForm
      onSubmit(formData);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        company: "",
        projectType: "",
        collaborationType: selectedCollaborationType || "contract-work",
        budgetRange: "",
        timeline: "",
        projectTitle: "",
        projectDescription: "",
        technicalRequirements: "",
        goals: "",
        agreeToTerms: false,
      });
      setErrors({});
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({ ...prev, projectDescription: "Failed to send. Please try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-surface p-6 rounded-lg border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="User" size={20} />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="John Doe"
            value={formData.fullName}
            onChange={(e) => handleChange("fullName", e.target.value)}
            error={errors.fullName}
            required
          />
          <Input
            label="Email Address"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={errors.email}
            required
          />
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 000-0000"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            error={errors.phone}
            required
          />
          <Input
            label="Company/Organization"
            type="text"
            placeholder="Your Company Name"
            value={formData.company}
            onChange={(e) => handleChange("company", e.target.value)}
          />
        </div>
      </div>

      <div className="bg-surface p-6 rounded-lg border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Briefcase" size={20} />
          Project Details
        </h3>
        <div className="space-y-4">
          <Select
            label="Project Type"
            placeholder="Select project type"
            options={projectTypes.map((type) => ({
              value: type.id,
              label: type.name,
              description: type.description,
            }))}
            value={formData.projectType}
            onChange={(value) => handleChange("projectType", value as string)}
            error={errors.projectType}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Budget Range"
              placeholder="Select budget range"
              options={budgetRanges.map((range) => ({
                value: range.id,
                label: range.label,
                description: range.description,
              }))}
              value={formData.budgetRange}
              onChange={(value) => handleChange("budgetRange", value as string)}
              error={errors.budgetRange}
              required
            />
            <Select
              label="Timeline"
              placeholder="Select timeline"
              options={timelineOptions.map((option) => ({
                value: option.id,
                label: option.label,
                description: option.description,
              }))}
              value={formData.timeline}
              onChange={(value) => handleChange("timeline", value as string)}
              error={errors.timeline}
              required
            />
          </div>

          <Input
            label="Project Title"
            type="text"
            placeholder="Give your project a descriptive title"
            value={formData.projectTitle}
            onChange={(e) => handleChange("projectTitle", e.target.value)}
            error={errors.projectTitle}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Project Description <span className="text-destructive">*</span>
            </label>
            <textarea
              className="w-full px-4 py-3 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={5}
              placeholder="Describe your project in detail. What problem are you trying to solve? What are your goals?"
              value={formData.projectDescription}
              onChange={(e) =>
                handleChange("projectDescription", e.target.value)
              }
            />
            {errors.projectDescription && (
              <p className="mt-1 text-sm text-destructive">
                {errors.projectDescription}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Minimum 50 characters ({formData.projectDescription.length}/50)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Technical Requirements
            </label>
            <textarea
              className="w-full px-4 py-3 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={4}
              placeholder="List any specific technologies, platforms, or technical requirements"
              value={formData.technicalRequirements}
              onChange={(e) =>
                handleChange("technicalRequirements", e.target.value)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Goals & Success Metrics
            </label>
            <textarea
              className="w-full px-4 py-3 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={4}
              placeholder="What does success look like for this project? What are your key performance indicators?"
              value={formData.goals}
              onChange={(e) => handleChange("goals", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-surface p-6 rounded-lg border border-border">
        <Checkbox
          label="I agree to the terms and conditions"
          description="By submitting this form, you agree to our collaboration terms and privacy policy"
          checked={formData.agreeToTerms}
          onChange={(e) => handleChange("agreeToTerms", e.target.checked)}
          error={errors.agreeToTerms}
          required
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          className="flex-1"
        >
          Submit Collaboration Request
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          iconName="MessageCircle"
          iconPosition="left"
          onClick={() => window.open("https://wa.me/qr/MIPMGLGF4RZ5O1", "_blank")}
        >
          Quick Chat on WhatsApp
        </Button>
      </div>
    </form>
  );
};

export default CollaborationForm;